import SiteLayout from "../components/SiteLayout";
import SEO from "../components/SEO";
import CTABanner from "../components/CTABanner";
import { Heart, Compass, ShieldCheck, Sparkles, Users } from "lucide-react";

const values = [
 { icon: Heart, title: "Compassion first", desc: "Every visit begins with listening, and a refusal to judge what we hear." },
 { icon: Compass, title: "Person-centred", desc: "Your goals, history, and culture shape your plan. Not the other way around." },
 { icon: ShieldCheck, title: "Clinical rigour", desc: "Evidence-based care delivered with care that's quietly excellent." },
 { icon: Users, title: "Collaborative", desc: "We coordinate with therapists, primary care, and family when invited, you stay at the centre." },
 { icon: Sparkles, title: "Quietly premium", desc: "Unhurried sessions, clear communication, and a polish that respects your time." },
];

export default function About() {
 return (
 <SiteLayout>
 <SEO
 title="About Our Practice"
 description="Meet the clinicians behind Golden Life Standard Health. Our Maryland-licensed team blends evidence-based psychiatry with unhurried, dignified virtual care."
 path="/about"
 />
 <section data-testid="about-hero" className="container-pad max-w-7xl mx-auto pt-14 lg:pt-20 pb-12">
 <div className="max-w-3xl">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> About us</p>
 <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink text-balance">
 We exist to make excellent psychiatric care <span className="italic text-sage-600">feel personal again.</span>
 </h1>
 <p className="mt-6 text-lg text-muted_text leading-relaxed max-w-2xl">
 Golden Life Standard Health was founded on a simple belief: when mental health care is warm, accessible, and clinically rigorous, people don't just stabilise, they grow.
 </p>
 </div>
 </section>

 <section data-testid="about-image" className="container-pad max-w-7xl mx-auto pb-16">
 <img
 src="https://images.unsplash.com/photo-1684065022492-145915adcc47?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHxjYWxtJTIwbmF0dXJlJTIwb2NlYW4lMjBzYWdlJTIwZ3JlZW58ZW58MHx8fHwxNzc4MDAyMzgwfDA&ixlib=rb-4.1.0&q=85"
 alt="A calm shoreline with sage greens and warm light"
 className="w-full h-[340px] sm:h-[440px] object-cover rounded-3xl"
 />
 </section>

 <section data-testid="about-story" className="container-pad max-w-7xl mx-auto pb-20">
 <div className="grid lg:grid-cols-12 gap-12">
 <div className="lg:col-span-5">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Our story</p>
 <h2 className="mt-5 font-heading text-3xl sm:text-4xl tracking-tight text-ink">
 Built by clinicians who saw the gaps, and chose to close them.
 </h2>
 </div>
 <div className="lg:col-span-7 space-y-5 text-muted_text leading-relaxed">
 <p>For too many people, getting psychiatric care has meant long waits, brief visits, and a sense of being processed rather than known. We built Golden Life Standard Health to be the alternative we'd want for our own families.</p>
 <p>Our practice is virtual by design, not because it's convenient (though it is), but because it lets us serve people where they actually live: between school pickups, after late shifts, in the quiet of their own homes.</p>
 <p>Every patient is seen by a board-certified psychiatric clinician. Every plan is built collaboratively. Every follow-up is intentional. That's what we mean by a golden standard.</p>
 </div>
 </div>
 </section>

 <section data-testid="about-mission" className="bg-paper border-y border-[#E8EBE4]">
 <div className="container-pad max-w-7xl mx-auto py-20">
 <div className="grid lg:grid-cols-12 gap-10">
 <div className="lg:col-span-5">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Our mission</p>
 <h2 className="mt-5 font-heading text-3xl sm:text-4xl tracking-tight text-ink text-balance">
 Compassionate, person-centred mental health care that builds stability, resilience, and long-term wellbeing.
 </h2>
 </div>
 <div className="lg:col-span-7 space-y-4 text-muted_text leading-relaxed">
 <p>We believe mental wellness deserves the same standard of care as any other field of medicine, and the same dignity as any other appointment. That means clear communication, evidence-based treatment, and clinicians who treat you like a whole person.</p>
 <p>We're committed to reducing stigma, removing access barriers, and building a model of care that earns trust quietly, visit after visit.</p>
 </div>
 </div>
 </div>
 </section>

 <section data-testid="about-values" className="container-pad max-w-7xl mx-auto section">
 <div className="text-center max-w-2xl mx-auto">
 <p className="eyebrow justify-center"><span className="w-6 h-px bg-gold-500" /> Values</p>
 <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink">What we stand for</h2>
 </div>
 <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
 {values.map((v, i) => (
 <div key={v.title} data-testid={`value-${i}`} className="card-soft p-7 hover:-translate-y-1 transition-transform duration-300">
 <div className="w-12 h-12 rounded-2xl bg-sage-50 flex items-center justify-center text-sage-700">
 <v.icon className="w-6 h-6" strokeWidth={1.5} />
 </div>
 <h3 className="mt-6 font-heading text-xl font-semibold text-ink">{v.title}</h3>
 <p className="mt-3 text-sm text-muted_text leading-relaxed">{v.desc}</p>
 </div>
 ))}
 </div>
 </section>

 <CTABanner headline="Care that meets you with warmth, and works." sub="From first call to long-term follow-up, the standard never slips." />
 </SiteLayout>
 );
}
